/**
 * Het tabblad Tekstlengte: hoeveel woorden een tekst op elk niveau heeft
 * (backend: services/tekstlengtes.py).
 *
 * Per niveau het aantal woorden, de standaard uit de code, het leestempo
 * (de lijn door de woorden per minuut van de AVI-kaarten) en de leestijd die
 * daaruit volgt, zodat je ziet wat een andere lengte voor het lezen
 * betekent. Opslaan stuurt alleen wat veranderde.
 *
 * Een nieuwe lengte geldt voor wat daarna gemaakt wordt. Een goedgekeurde
 * versie blijft gelden, met haar eigen lengte; de kolom "Andere lengte" telt
 * ze, en het tabblad Niveauteksten laat ze opnieuw maken.
 */
import React, { useEffect, useState } from 'react';
import { RotateCcw, Save } from 'lucide-react';
import { beheerApi as api } from '../../../api/beheer';
import { Draaier, Laden } from '../../../components/Laden';

const getal = (x, cijfers = 1) => x.toFixed(cijfers).replace('.', ',');
const tijd = (iso) => (iso ? new Date(iso).toLocaleDateString('nl-NL') : '');

export function Tekstlengte({ actief, meldFout }) {
  const [gegevens, setGegevens] = useState(null);
  const [invoer, setInvoer] = useState({});         // per niveau wat in het veld staat (tekst)
  const [bewaren, setBewaren] = useState(false);
  const [bericht, setBericht] = useState('');

  const zetGegevens = (data) => {
    setGegevens(data);
    setInvoer(Object.fromEntries(data.niveaus.map(n => [n.niveau, String(n.woorden)])));
  };

  useEffect(() => {
    if (!actief) return;
    api.haalTekstlengtes().then(zetGegevens).catch(err => meldFout(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen van het tabblad
  }, [actief]);

  if (!gegevens) return <Laden tekst="Tekstlengtes laden..." />;

  const { minimum, maximum, minimum_verhaal: minimumVerhaal } = gegevens;
  const waarde = (niveau) => Number(invoer[niveau]);
  const geldig = (niveau) => /^\d+$/.test(invoer[niveau] || '') && waarde(niveau) >= minimum && waarde(niveau) <= maximum;
  const veranderd = gegevens.niveaus.filter(n => geldig(n.niveau) && waarde(n.niveau) !== n.woorden);
  const ongeldig = gegevens.niveaus.filter(n => !geldig(n.niveau));
  const korterDanErvoor = gegevens.niveaus.filter((n, i) => i > 0 && geldig(n.niveau)
    && geldig(gegevens.niveaus[i - 1].niveau) && waarde(n.niveau) < waarde(gegevens.niveaus[i - 1].niveau));

  const bewaar = async () => {
    setBewaren(true);
    setBericht('');
    try {
      const data = await api.bewaarTekstlengtes(Object.fromEntries(veranderd.map(n => [n.niveau, waarde(n.niveau)])));
      zetGegevens(data);
      setBericht(`Bewaard: ${veranderd.map(n => `${n.niveau} ${waarde(n.niveau)}`).join(', ')}. Wat nu gemaakt wordt, krijgt deze lengte;`
        + ' een goedgekeurde versie blijft gelden tot je haar opnieuw laat maken (tabblad Niveauteksten).');
    } catch (err) {
      meldFout(err.message);
    } finally {
      setBewaren(false);
    }
  };

  const terugNaarStandaard = () => setInvoer(Object.fromEntries(gegevens.niveaus.map(n => [n.niveau, String(n.standaard)])));

  return (
    <div className="card">
      <h2 className="mb-8">Tekstlengte</h2>
      <p className="text-muted mb-16" style={{ fontSize: 14 }}>
        Hoeveel woorden een tekst op elk niveau heeft. De standaard gaat uit van een gelijke leestijd: het leestempo
        van de AVI-kaarten (een lijn van {getal(gegevens.niveaus[0].leestempo)} woorden per minuut bij M4
        tot {getal(gegevens.niveaus[gegevens.niveaus.length - 1].leestempo)} bij Plus), maal ongeveer zes minuten.
        Een lengte geldt voor wat daarna gemaakt wordt; een goedgekeurde versie blijft gelden tot je haar in het
        tabblad Niveauteksten opnieuw laat maken. Tussen {minimum} en {maximum} woorden; een verhaal in vijf
        deelteksten heeft er minstens {minimumVerhaal}.
      </p>

      {bericht && <div className="alert alert-success mb-16">{bericht}</div>}

      <table>
        <thead>
          <tr>
            <th>Niveau</th>
            <th>Woorden</th>
            <th>Standaard</th>
            <th>Leestempo (woorden per minuut)</th>
            <th>Leestijd</th>
            <th title="Goedgekeurde versies die met een andere lengte gemaakt zijn dan nu is ingesteld">Andere lengte</th>
            <th>Gewijzigd</th>
          </tr>
        </thead>
        <tbody>
          {gegevens.niveaus.map(n => {
            const ok = geldig(n.niveau);
            return (
              <tr key={n.niveau} data-testid={`lengte-${n.niveau}`}>
                <td><strong>{n.niveau}</strong></td>
                <td>
                  <input type="number" className="input" min={minimum} max={maximum} step={25} aria-label={`Woorden ${n.niveau}`}
                    value={invoer[n.niveau] ?? ''} onChange={(e) => setInvoer(prev => ({ ...prev, [n.niveau]: e.target.value }))}
                    style={{ width: 100, padding: '4px 8px', borderColor: ok ? undefined : 'var(--fout)',
                      background: ok && waarde(n.niveau) !== n.woorden ? 'var(--waarschuw-vlak)' : undefined }} />
                </td>
                <td className="text-muted">{n.standaard}</td>
                <td className="text-muted">{getal(n.leestempo)}</td>
                <td>{ok ? `ca. ${getal(waarde(n.niveau) / n.leestempo)} min` : '-'}</td>
                <td>{n.andere_lengte > 0 ? <span className="badge badge-warning">{n.andere_lengte} versie(s)</span> : <span className="text-muted">-</span>}</td>
                <td className="text-muted" style={{ fontSize: 12 }}>
                  {n.gewijzigd_op ? `${tijd(n.gewijzigd_op)}${n.gewijzigd_door ? `, ${n.gewijzigd_door}` : ''}` : 'standaard'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {ongeldig.length > 0 && (
        <p className="mt-8" style={{ color: 'var(--fout)', fontSize: 13 }}>
          {ongeldig.map(n => n.niveau).join(', ')}: vul een aantal woorden in tussen {minimum} en {maximum}.
        </p>
      )}
      {korterDanErvoor.length > 0 && (
        <p className="mt-8" style={{ color: 'var(--waarschuw-tekst)', fontSize: 13 }}>
          Let op: {korterDanErvoor.map(n => n.niveau).join(', ')} {korterDanErvoor.length === 1 ? 'is' : 'zijn'} korter dan het niveau ervoor.
        </p>
      )}

      <div className="flex gap-8 mt-16">
        <button className="btn btn-success" onClick={bewaar} disabled={bewaren || veranderd.length === 0 || ongeldig.length > 0}>
          {bewaren ? <Draaier /> : <Save size={16} />} Opslaan{veranderd.length > 0 && ` (${veranderd.length})`}
        </button>
        <button className="btn btn-secondary" onClick={terugNaarStandaard} disabled={bewaren}>
          <RotateCcw size={16} /> Terug naar de standaard
        </button>
      </div>
    </div>
  );
}
